
import { useState, useEffect } from 'react';
import { Activity, AlertCircle, CheckCircle, Clock, Settings } from 'lucide-react';
import api from '../api/axios';

export default function EyeAISystem() {
  const [systemStatus, setSystemStatus] = useState({
    api: 'operational',
    database: 'operational',
    aiEngine: 'operational',
    lastUpdated: new Date()
  });
  
  const [maintenanceSchedule, setMaintenanceSchedule] = useState([]);

  // Simulate status check every 30 seconds
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await api.get('/webapi/status');
        const data = response.data;

        setSystemStatus(prev => ({
          ...prev,
          database: data.database === 'connected' ? 'operational' : 'down',
          aiEngine: data.aiService === 'available' ? 'operational' : 'down',
          lastUpdated: new Date()
        }));

        const schedule = [];
        if (data.DBscheduledDate) {
          schedule.push({
            id: 1,
            title: 'Database Optimization',
            scheduledDate: data.DBscheduledDate,
            duration: data.DBduration,
            status: 'scheduled'
          });
        }
        if (data.AIscheduledDate) {
          schedule.push({
            id: 2,
            title: 'AI Model Update',
            scheduledDate: data.AIscheduledDate,
            duration: data.AIduration,
            status: 'scheduled'
          });
        }
        setMaintenanceSchedule(schedule);
      } catch (error) {
        console.error('Error fetching status:', error);
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);

    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'operational':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'degraded':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'down':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Activity className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'operational':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'degraded':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'down':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="p-8">


      {/* Header Section */}
      <div className="bg-white rounded-lg shadow-sm p-8 mb-8 max-w-6xl">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">EyeAI System</h1>
        <p className="text-gray-600 mb-6"></p>
        <div className="grid md:grid-rows-3 gap-6 text-left">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h2 className="text-2xl font-bold mb-3 text-blue-900">Vision</h2>
            <p className="text-gray-700">
              Eye AI Pte Ltd is dedicated to revolutionizing early disease detection through AI-driven healthcare solutions.
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h2 className="text-2xl font-bold mb-3 text-blue-900">Mission</h2>
            <p className="text-gray-700">
              Promote health examination adoption for early disease detection.
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h2 className="text-2xl font-bold mb-3 text-blue-900">Goal</h2>
            <p className="text-gray-700">
              Our goal is to make preventive screening accessible, accurate, and scalable, ensuring early intervention for millions at risk of vision impairment and systemic diseases.
            </p>
          </div>
        </div>
      </div>

      {/* System Status Section */}
      <div className="bg-white rounded-lg shadow-sm p-8 mb-8 max-w-6xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <Activity className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-800">System Status</h2>
          </div>
          <div className="text-xs text-gray-500">
            Last updated: {systemStatus.lastUpdated.toLocaleTimeString()}
          </div>
        </div>
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-2">
            {getStatusIcon(systemStatus.api)}
            <span className="text-sm text-gray-600">API</span>
          </div>
          <div className="flex items-center gap-2">
            {getStatusIcon(systemStatus.database)}
            <span className="text-sm text-gray-600">Database</span>
          </div>
          <div className="flex items-center gap-2">
            {getStatusIcon(systemStatus.aiEngine)}
            <span className="text-sm text-gray-600">AI Engine</span>
          </div>
        </div>
      </div>

      {/* Maintenance Schedule Section */}
      <div className="bg-white rounded-lg shadow-sm p-8 max-w-6xl">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2 mb-6">
          <Settings className="w-6 h-6 text-blue-600" />
          Maintenance Schedule
        </h2>
        
        <div className="space-y-4">
          {maintenanceSchedule.map((item) => (
            <div
              key={item.id}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800 mb-2">{item.title}</h3>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {item.scheduledDate}
                    </div>
                    <div>Duration: {item.duration}</div>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(item.status)}`}>
                  {item.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}